import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

// Supported languages configuration (BCP 47 format: language-REGION)
export const SUPPORTED_LANGUAGES = {
  'en-US': { code: 'en-US', name: 'English', nativeName: 'English' },
  'zh-CN': { code: 'zh-CN', name: 'Simplified Chinese', nativeName: '简体中文' },
  'zh-TW': { code: 'zh-TW', name: 'Traditional Chinese', nativeName: '繁體中文' },
  'ja-JP': { code: 'ja-JP', name: 'Japanese', nativeName: '日本語' },
  'es-ES': { code: 'es-ES', name: 'Spanish', nativeName: 'Español' },
  'fr-FR': { code: 'fr-FR', name: 'French', nativeName: 'Français' },
} as const;

export type LanguageCode = keyof typeof SUPPORTED_LANGUAGES;

export interface LanguageState {
  // Current language
  currentLanguage: LanguageCode;
  
  // Loading states
  isLoadingLanguage: boolean;
  
  // Error handling
  languageError: string | null;
  
  // Initialization tracking
  isLanguageInitialized: boolean;
  
  // Available languages
  availableLanguages: typeof SUPPORTED_LANGUAGES;
}

const initialState: LanguageState = {
  currentLanguage: 'en-US',
  isLoadingLanguage: false,
  languageError: null,
  isLanguageInitialized: false,
  availableLanguages: SUPPORTED_LANGUAGES,
};

const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    // Set language
    setLanguage: (state, action: PayloadAction<LanguageCode>) => {
      state.currentLanguage = action.payload;
      state.languageError = null;
    },
    
    // Loading states
    startLoadingLanguage: (state) => {
      state.isLoadingLanguage = true;
      state.languageError = null;
    },
    
    completeLoadingLanguage: (state, action: PayloadAction<LanguageCode>) => {
      state.isLoadingLanguage = false;
      state.currentLanguage = action.payload;
      state.languageError = null;
      state.isLanguageInitialized = true;
    },
    
    failLoadingLanguage: (state, action: PayloadAction<string>) => {
      state.isLoadingLanguage = false;
      state.languageError = action.payload;
    },
    
    // Error handling
    clearLanguageError: (state) => {
      state.languageError = null;
    },
    
    // Reset language to default
    resetLanguage: (state) => {
      state.currentLanguage = 'en-US';
      state.isLoadingLanguage = false;
      state.languageError = null;
      state.isLanguageInitialized = false;
    },
    
    // Mark as initialized
    markLanguageInitialized: (state) => {
      state.isLanguageInitialized = true;
    },
  },
});

export const {
  setLanguage,
  startLoadingLanguage,
  completeLoadingLanguage,
  failLoadingLanguage,
  clearLanguageError,
  resetLanguage,
  markLanguageInitialized,
} = languageSlice.actions;

export default languageSlice.reducer;

// Selectors
export const selectLanguage = (state: { language: LanguageState }) => state.language;
export const selectCurrentLanguage = (state: { language: LanguageState }) => state.language.currentLanguage;
export const selectIsLoadingLanguage = (state: { language: LanguageState }) => state.language.isLoadingLanguage;
export const selectLanguageError = (state: { language: LanguageState }) => state.language.languageError;
export const selectIsLanguageInitialized = (state: { language: LanguageState }) => state.language.isLanguageInitialized;
export const selectAvailableLanguages = (state: { language: LanguageState }) => state.language.availableLanguages;

// Computed selectors
export const selectCurrentLanguageName = (state: { language: LanguageState }) => {
  const code = state.language.currentLanguage;
  return SUPPORTED_LANGUAGES[code].name;
};

export const selectCurrentLanguageNativeName = (state: { language: LanguageState }) => {
  const code = state.language.currentLanguage;
  return SUPPORTED_LANGUAGES[code].nativeName;
};

export const selectAvailableLanguagesList = createSelector(
  [selectAvailableLanguages],
  (availableLanguages) => Object.values(availableLanguages)
);
